import React, { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useGetUserNotificationsQuery, useOnNotificationCreatedSubscription } from "@axio/graphql/generated/graphql";
import { useAuth } from "../hooks/useAuth";

type NotificationContextType = {
  notificationsVisible: boolean;
  setNotificationsVisible: (visible: boolean) => void;
  toggleNotifications: () => void;
  notifications: Array<{
    id: string;
    isRead: boolean;
    title: string;
    message: string;
    created: string;
    actionUrl?: string | null;
    type?: string;
  }>;
  hasUnreadNotification: boolean;
  refetch: () => void;
};

const NotificationContext = createContext<NotificationContextType>({
  notificationsVisible: false,
  setNotificationsVisible: () => {},
  toggleNotifications: () => {},
  notifications: [],
  hasUnreadNotification: false,
  refetch: () => {},
});

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const { userInfo, isAuthenticated, isAuthReady } = useAuth();
  const shouldFetchNotifications = Boolean(isAuthenticated && isAuthReady && userInfo.userId);

  const { data: notificationsData, refetch } = useGetUserNotificationsQuery({
    fetchPolicy: "network-only",
    skip: !shouldFetchNotifications,
  });

  const notifications = notificationsData?.getUserNotifications?.data ?? [];

  const hasUnreadNotification = useMemo(() => {
    return notifications.some((notification) => notification.isRead !== true);
  }, [notifications]);

  const toggleNotifications = () => setNotificationsVisible((prev) => !prev);

  useOnNotificationCreatedSubscription({
    skip: !shouldFetchNotifications,
    variables: { userId: userInfo.userId },
    onData: (result) => {
      if (result.data?.data?.onNotificationCreated) {
        refetch();
      }
    },
  });

  return (
    <NotificationContext.Provider
      value={{
        notificationsVisible,
        setNotificationsVisible,
        toggleNotifications,
        notifications,
        hasUnreadNotification,
        refetch,
      }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  return useContext(NotificationContext);
};
